import React, { useState } from "react";
import PropTypes from "prop-types";
import { useController } from "@rest-hooks/react";
import { useParams } from "react-router-dom";

import CheckinEventResource from "resources/checkin/EventResource";
import OrganizationEventResource from "resources/organization/EventResource";

import Content from "./Content";
import ButtonGroup from "./ButtonGroup";
import { handleValidation } from "./handleValidation";

import { useError } from "utils/useErrorController";
import { useToast } from "utils/context/ToastContext";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

const NewTicketModal = ({ open, onClose, tickets, done, TicketResource, ticketResourceParams, itemNoun = "Ticket", checkedInLabel = "Mark as checked in" }) => {

  const [email, setEmail] = useState("");
  const [ticket, setTicket] = useState(tickets?.[0]);
  const [variant, setVariant] = useState(null);
  const [amount, setAmount] = useState(tickets?.[0]?.min_amount || 1);
  const [checkedInStatus, setCheckedInStatus] = useState(true);

  const { handleError, setLoading, loading } = useError();
  const { showToast } = useToast();
  const { fetch } = useController();
  const { organizationId, eventId, eventUuid } = useParams();

  const EventResource = organizationId ? OrganizationEventResource : CheckinEventResource;
  
  const successMessage = `${amount} ${itemNoun}${amount > 1 ? 's' : ''} Sold`

  const handleNewTicketSubmit = async () => {
    try {
      setLoading(true);
      handleValidation(email, ticket.id, ticket, variant);

      await fetch(
        TicketResource.create(),
        ticketResourceParams,
        {
          email: email,
          ticket_id: ticket.id,
          product_variant_id: variant?.id,
          amount: amount,
          checked_in: checkedInStatus
        }
      );

      // fetch event details to refresh tickets list and available amounts
      await fetch(EventResource.detail(), { organization_id: organizationId, id: eventId || eventUuid });

      done();
      showToast("success", successMessage);
      handleClose();
      setLoading(false);
    } catch (error) {
      setLoading(false);
      handleError(error);
    }
  };

  const handleClose = () => {
    setEmail("");
    setTicket(tickets?.[0]);
    setVariant(null);
    setAmount(1);
    onClose();
  };

  return (
    <Dialog open={open} fullWidth={true} maxWidth="sm" className="new-ticket-modal" >
      <DialogTitle>
        Register Cash Sale
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 12,
            color: "black",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Content
          onEmailChange={setEmail}
          tickets={tickets}
          onTicketSelect={setTicket}
          variant={variant}
          onVariantSelect={setVariant}
          ticket={ticket}
          amount={amount}
          onAmountChange={setAmount}
          checkedInStatus={checkedInStatus}
          onCheckedInStatusChange={setCheckedInStatus}
          itemNoun={itemNoun}
          checkedInLabel={checkedInLabel}
        />
      </DialogContent>
      <DialogActions sx={{ display: "flex" }}>
        <ButtonGroup
          handleClose={handleClose}
          handleNewTicketSubmit={handleNewTicketSubmit}
          loading={loading}
          disabled={tickets.length === 0}
        />
      </DialogActions>
    </Dialog>
  );
};

NewTicketModal.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool,
  tickets: PropTypes.array,
  done: PropTypes.func,
  TicketResource: PropTypes.object,
  ticketResourceParams: PropTypes.object,
  intent: PropTypes.string,
  itemNoun: PropTypes.string,
  checkedInLabel: PropTypes.string
};

export default NewTicketModal;
